import { apiClient } from "./api-client";
import { toArray } from "./api-utils";
import type { TriyoTask } from "@/types/models";

export type TaskGridCategory = "Active" | "Completed" | "Archived";
export type TaskListKind = "static" | "configurable";

interface TaskGridRow {
  task?: unknown;
}

function parseTaskGridRow(row: unknown): TriyoTask | null {
  let value = row;
  if (value && typeof value === "object" && "task" in value) value = (value as TaskGridRow).task;

  if (typeof value === "string") {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }

  if (!value || typeof value !== "object" || Array.isArray(value)) return null;

  // Configurable task grids wrap every property in a ColumnDefinition whose
  // `Data` member is the value rendered by Triyo.Web. Static grids are already
  // plain DTOs, so this transformation is intentionally field-by-field.
  const normalized: Record<string, unknown> = {};
  for (const [key, field] of Object.entries(value)) {
    normalized[key] =
      field && typeof field === "object" && !Array.isArray(field) && "Data" in field
        ? (field as { Data?: unknown }).Data
        : field;
  }
  return normalized as TriyoTask;
}

function configuredTaskListKind(): TaskListKind | null {
  const configured = window.APP_SETTINGS?.TASK_LIST?.trim().toLowerCase();
  if (configured === "static" || configured === "configurable") return configured;
  return null;
}

export const TasksApi = {
  /**
   * Active tasks only, unpaged — the legacy add-in endpoint. Kept for callers
   * that just need the user's live work.
   */
  getUserTasks: async (): Promise<TriyoTask[]> => {
    const res = await apiClient.get<unknown>("/tasks/GetUserTaskListForAddin");
    return toArray<TriyoTask>(res.data, "Tasks", "tasks");
  },

  /** Resolve the same static/configurable task-list branch Triyo.Web uses. */
  resolveTaskListKind: async (companyId?: number): Promise<TaskListKind> => {
    const configured = configuredTaskListKind();
    if (configured) return configured;
    if (!companyId) return "static";

    try {
      const res = await apiClient.get<unknown>("/settings/GetColumnDefinitionByCompany", {
        params: { Id: companyId },
      });
      return typeof res.data === "string" && res.data.length > 0 ? "configurable" : "static";
    } catch {
      // This is Triyo.Web's fallback when the company-setting lookup fails.
      return "static";
    }
  },

  /** Complete status-specific task list from the endpoint used by Triyo.Web. */
  getUserTasksForCategory: async (category: TaskGridCategory, kind: TaskListKind): Promise<TriyoTask[]> => {
    const endpoint =
      kind === "configurable" ? "/Tasks/GetUserTasksForConfigurableTelerikGrid" : "/Tasks/GetUserTasksForTelerikGrid";
    const res = await apiClient.get<unknown>(endpoint, { params: { type: category } });
    return toArray<unknown>(res.data, "data", "Data")
      .map(parseTaskGridRow)
      .filter((task): task is TriyoTask => task !== null);
  },
};
